import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Women In Wealth Summit",
    "description": "Learn about the vision behind the Women In Wealth Summit, curated by the WealthRise Management team.",
    "mainEntity": {
      "@type": "Organization",
      "name": "Wealthrise Management",
      "description": "The WealthRise Management team brings together a unique perspective shaped by work across six global cities, bridging cultures and capital with precision."
    }
  };

  const differentiators = [
    {
      number: '01',
      title: 'Curated, not crowded',
      description: 'Every participant is selected to ensure alignment in values, ambition, and discretion.',
    },
    {
      number: '02',
      title: 'Cross cultural by design',
      description: "A bridge between Paris, Dubai, Riyadh, Geneva, Beirut, and London — reflecting the team's global footprint.",
    },
    {
      number: '03',
      title: 'From inspiration to architecture',
      description: "You leave with frameworks, not just ideas — a signature of WealthRise's approach.",
    },
    {
      number: '04',
      title: 'Discretion above all',
      description: 'No mass stages. No performative networking. Just intimate, meaningful conversations curated by the host.',
    },
  ];

  const cities = ['Paris', 'Dubai', 'Riyadh', 'Geneva', 'Beirut', 'London'];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About the Summit | Women In Wealth · Beirut 2026"
        description="Discover the vision behind the Women In Wealth Summit. Meet host Racha Mezher, founder of Wealthrise Management, and learn what makes this curated gathering unique. A bridge between Paris, Dubai, Riyadh, Geneva, Beirut, and London."
        keywords="Racha Mezher, Wealthrise Management, women in wealth about, summit vision, curated summit, cross cultural wealth management, family office leadership"
        canonical={`${siteUrl}/about`}
        schema={schema}
      />
      <Navigation />

      {/* Hero - Full Bleed Editorial */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c780d73d3f733151e0f9c18bf4f570eb.jpg"
            alt="Summit Vision"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent"></div>
        </div>

        <div className="relative z-10 px-8 lg:px-16 xl:px-24 py-32 max-w-4xl">
          <p className="text-champagne-400 text-sm uppercase tracking-[0.4em] mb-8 font-medium">
            The Vision
          </p>
          <h1 className="text-5xl md:text-6xl lg:text-8xl font-display font-bold text-white leading-[1.05] mb-8">
            Women are no longer beneficiaries of wealth
          </h1>
          <div className="flex items-center gap-6 mb-8">
            <div className="w-24 h-px bg-champagne-400"></div>
            <p className="text-2xl md:text-3xl text-white/90 font-light">
              They are architects
            </p>
          </div>
          <p className="text-lg text-white/70 leading-relaxed max-w-xl">
            Of capital, governance, and culture. This summit is built for those who navigate numbers, narratives, and cross cultural expectations with precision and grace.
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-white/50">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent animate-pulse"></div>
        </div>
      </section>

      {/* What Makes Different - Horizontal Scroll Cards */}
      <section className="py-32 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="px-8 lg:px-16 xl:px-24 mb-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div>
                <p className="text-champagne-600 text-sm uppercase tracking-[0.3em] mb-4 font-medium">
                  Our Difference
                </p>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-slate-900">
                  What Makes This<br />Summit Different
                </h2>
              </div>
              <p className="text-lg text-slate-600 max-w-md leading-relaxed">
                A gathering designed with intention, where every detail reflects our commitment to excellence.
              </p>
            </div>
          </div>
        </div>

        <div className="px-8 lg:px-16 xl:px-24">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {differentiators.map((item, index) => (
                <div
                  key={index}
                  className="group relative bg-white rounded-3xl p-8 lg:p-10 border border-slate-100 hover:border-champagne-200 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 overflow-hidden"
                >
                  {/* Large Background Number */}
                  <span className="absolute -top-8 -right-4 text-[180px] font-display font-bold text-slate-50 leading-none select-none group-hover:text-champagne-50 transition-colors duration-500">
                    {item.number}
                  </span>
                  
                  <div className="relative">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-sm font-medium text-champagne-600 bg-champagne-50 px-4 py-2 rounded-full">
                        {item.number}
                      </span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4 group-hover:text-champagne-700 transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-base lg:text-lg">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Host Section - Asymmetric Split */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
          {/* Image Side */}
          <div className="lg:col-span-5 relative h-[500px] lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20businesswoman%20in%20sophisticated%20setting%20confident%20leadership%20presence%20refined%20aesthetic%20editorial%20photography%20warm%20neutral%20tones%20executive%20style%20luxury%20professional%20environment%20natural%20lighting&width=800&height=1000&seq=host-new-001&orientation=portrait"
              alt="Racha Mezher"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-slate-900/20"></div>
            
            {/* Floating Name Card - Mobile */}
            <div className="absolute bottom-8 left-8 right-8 lg:hidden bg-white/95 backdrop-blur-sm rounded-2xl p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-champagne-600 mb-2 font-medium">Your Host</p>
              <h3 className="text-3xl font-display font-bold text-slate-900">Racha Mezher</h3>
              <p className="text-sm text-slate-600 mt-1">Founder & CEO, Wealthrise Management</p>
            </div>
          </div>

          {/* Content Side */}
          <div className="lg:col-span-7 bg-slate-900 px-8 lg:px-16 xl:px-20 py-20 lg:py-32 flex items-center">
            <div className="max-w-xl">
              {/* Desktop Name */}
              <div className="hidden lg:block mb-12">
                <p className="text-champagne-400 text-sm uppercase tracking-[0.3em] mb-4 font-medium">Your Host</p>
                <h3 className="text-5xl xl:text-6xl font-display font-bold text-white mb-2">Racha Mezher</h3>
                <p className="text-lg text-slate-400">Founder & CEO, Wealthrise Management</p>
              </div>

              <div className="w-16 h-px bg-champagne-500 mb-10"></div>

              <div className="space-y-6 text-slate-300 leading-relaxed">
                <p className="text-lg">
                  Racha Mezher brings together a unique perspective shaped by her work across six global cities, bridging cultures and capital with precision.
                </p>
                <p>
                  As the founder of Wealthrise Management, she has dedicated her career to helping women navigate the complexities of wealth, governance, and legacy with clarity and confidence.
                </p>
                <p className="text-champagne-300 italic border-l-2 border-champagne-500 pl-6">
                  "This summit reflects my commitment to creating spaces where meaningful conversations lead to actionable frameworks."
                </p>
              </div>

              {/* Cities */}
              <div className="mt-12">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Global Footprint</p>
                <div className="flex flex-wrap gap-3">
                  {cities.map((city, index) => (
                    <span
                      key={index}
                      className="px-5 py-2.5 bg-white/5 border border-white/10 rounded-full text-sm text-slate-300 hover:bg-champagne-500/10 hover:border-champagne-500/30 hover:text-champagne-300 transition-all duration-300 cursor-default"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 lg:px-16 bg-gradient-to-br from-champagne-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-6">
            Ready to Join the Conversation?
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            A curated room, by application only. Hosted and personally curated to ensure alignment in values, ambition, and discretion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/attend"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Request an Invitation
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
            <Link
              to="/program"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-slate-900 rounded-full font-medium border border-slate-200 hover:border-champagne-300 hover:bg-champagne-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Program
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
